import { useEffect, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Phone,
  PhoneIncoming,
  User,
  Building2,
  Briefcase,
  ExternalLink,
  CalendarPlus,
  CheckCircle,
  Clock,
  XCircle,
  UserPlus,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatPhoneForDisplay } from '@/lib/phone-mask';
import { useLookupByPhone } from '@/hooks/sonax/useLookupByPhone';
import {
  useCreateIncomingCallActivity,
  useCompleteIncomingCallActivity,
  type CallOutcome,
} from '@/hooks/sonax/useIncomingCallActivity';
import { expandWidget, collapseWidget } from '@/utils/sonaxWidgetControl';

interface IncomingCallDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Telefone do que está ligando, como veio do widget (sem mascara, ex: "19987711234"). */
  phone: string | null;
}

interface OutcomeButtonSpec {
  outcome: CallOutcome;
  label: string;
  icon: typeof CheckCircle;
  className?: string;
}

const OUTCOMES: OutcomeButtonSpec[] = [
  { outcome: 'connected', label: 'Conexão feita', icon: CheckCircle },
  { outcome: 'meeting_scheduled', label: 'Reunião agendada', icon: CalendarPlus },
  { outcome: 'callback_later', label: 'Retornar depois', icon: Clock },
  { outcome: 'no_answer', label: 'Não atendeu', icon: XCircle, className: 'text-destructive' },
];

export function IncomingCallDrawer({ open, onOpenChange, phone }: IncomingCallDrawerProps) {
  const lookup = useLookupByPhone(phone);
  const createActivity = useCreateIncomingCallActivity();
  const completeActivity = useCompleteIncomingCallActivity();

  /**
   * Navega via window.history.pushState pra não recarregar a SPA.
   * Usamos isso em vez de useNavigate porque o drawer é montado FORA do
   * BrowserRouter (no SonaxWidgetProvider que é ancestral do router).
   * O React Router escuta `popstate`, então disparamos manualmente também.
   */
  const navigateTo = (path: string) => {
    if (window.location.pathname + window.location.search === path) return;
    window.history.pushState({}, '', path);
    // Notifica react-router que a URL mudou
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const [notes, setNotes] = useState('');
  const [activityId, setActivityId] = useState<string | null>(null);
  const [savedOutcome, setSavedOutcome] = useState<CallOutcome | null>(null);
  const createdForRef = useRef<string | null>(null);

  // Quando o drawer abre com um novo phone, cria a activity (uma vez por phone)
  useEffect(() => {
    if (!open || !phone || !lookup.data) return;
    if (createdForRef.current === phone) return;
    createdForRef.current = phone;

    createActivity.mutate(
      {
        phone,
        phoneNormalized: lookup.data.normalizedPhone,
        leadId: lookup.data.lead?.id ?? null,
        dealId: lookup.data.deal?.id ?? null,
        companyId: lookup.data.lead?.company_id ?? lookup.data.company?.id ?? null,
      },
      {
        onSuccess: (id) => setActivityId(id),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone, lookup.data]);

  // Reset state quando o drawer fecha
  useEffect(() => {
    if (!open) {
      setNotes('');
      setSavedOutcome(null);
      setActivityId(null);
      createdForRef.current = null;

      // Recolhe o widget Sonax junto com o drawer.
      // Próxima chamada recebida vai disparar expandWidget() de novo via
      // o MutationObserver em SonaxWidgetContext.
      collapseWidget();
      (window as unknown as { __sonaxWidgetOpen?: boolean }).__sonaxWidgetOpen = false;
    }
  }, [open]);

  // Body-class scoping pra reposicionar o widget Sonax pra DENTRO do slot
  // lateral do drawer (mesmo padrão do FocusExecutionMode com SonaxFrame).
  // CSS rules em index.css movem o widget pra `top: 132px right: 32px`
  // (alinhado com o slot reservado pela <aside> à direita do drawer).
  // Também garante que o widget esteja expandido (não minimizado) enquanto
  // o drawer está aberto.
  useEffect(() => {
    if (!open) {
      document.body.classList.remove('incoming-call-drawer-open');
      return;
    }
    document.body.classList.add('incoming-call-drawer-open');
    (window as unknown as { __sonaxWidgetOpen?: boolean }).__sonaxWidgetOpen = true;
    expandWidget();
    // Re-aplica em delays curtos pra vencer mutations que o Sonax pode fazer
    const t1 = setTimeout(() => expandWidget(), 200);
    const t2 = setTimeout(() => expandWidget(), 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      document.body.classList.remove('incoming-call-drawer-open');
    };
  }, [open]);

  const displayPhone = useMemo(() => (phone ? formatPhoneForDisplay(phone) : ''), [phone]);

  const handleOutcome = async (outcome: CallOutcome) => {
    if (!activityId) {
      // Activity ainda não foi criada — fecha sem persistir
      setSavedOutcome(outcome);
      onOpenChange(false);
      return;
    }
    try {
      await completeActivity.mutateAsync({
        activityId,
        outcome,
        notes: notes.trim() || undefined,
      });
      setSavedOutcome(outcome);
      onOpenChange(false);
    } catch {
      // toast já é gerido pelo hook
    }
  };

  const goToLead = () => {
    if (!lookup.data?.lead) return;
    onOpenChange(false);
    navigateTo(`/leads/${lookup.data.lead.id}`);
  };

  const goToDeal = () => {
    if (!lookup.data?.deal) return;
    onOpenChange(false);
    navigateTo(`/deals/${lookup.data.deal.id}`);
  };

  const goToCompany = () => {
    const companyId = lookup.data?.lead?.company?.id ?? lookup.data?.company?.id;
    if (!companyId) return;
    onOpenChange(false);
    navigateTo(`/companies/${companyId}`);
  };

  const goToCreateLead = () => {
    onOpenChange(false);
    // Pre-popular o phone via query string — LeadCreate pode ler isso
    navigateTo(`/leads/novo?phone=${encodeURIComponent(phone ?? '')}`);
  };

  const lookupKind = lookup.data?.kind ?? 'none';
  const lookupLoading = lookup.isLoading;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPrimitive.Portal>
        {/*
          Drawer NON-MODAL (modal={false}):
          - Sem overlay backdrop (não escurece a página)
          - Não captura cliques fora — usuário pode clicar no widget Sonax
            (atender/desligar) sem fechar o drawer
          - Não trapeia foco — input/notas funcionam, e botões do widget também
        */}
        <DialogPrimitive.Content
          // Previne fechamento ao clicar/interagir fora (na widget Sonax, no resto da page, etc).
          // Usuário só fecha pelo X ou pelos botões de outcome.
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          // Mantemos ESC fechando — comportamento padrão e útil.
          className={cn(
            'fixed inset-y-0 right-0 z-50 h-full w-full sm:max-w-3xl',
            'bg-background border-l shadow-xl flex flex-row',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
            'data-[state=open]:duration-300 data-[state=closed]:duration-200',
          )}
          aria-describedby={undefined}
        >
          {/* ======================================================
              Coluna esquerda: conteúdo (info + lookup + notes + outcomes)
              ====================================================== */}
          <div className="flex-1 min-w-0 flex flex-col">
          {/* Botão de fechar manual */}
          <DialogPrimitive.Close
            className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>

          {/* Header verde com info da chamada */}
          <div className="bg-success/10 border-b border-success/20 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center shrink-0">
                <PhoneIncoming className="w-6 h-6 text-success" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogPrimitive.Title className="text-base font-semibold">
                  Chamada recebida
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-mono text-foreground text-base mt-0.5">
                  {displayPhone || phone}
                </DialogPrimitive.Description>
              </div>
            </div>
          </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Card do match */}
          {lookupLoading ? (
            <Card>
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-40" />
              </CardContent>
            </Card>
          ) : lookupKind === 'none' ? (
            <Card className="border-dashed">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Nenhum cadastro encontrado</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Esse telefone não bate com nenhum lead, deal ou empresa da sua organização.
                </p>
                <Button size="sm" variant="outline" className="w-full" onClick={goToCreateLead}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Cadastrar como novo lead
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-4 space-y-3">
                {/* Lead */}
                {lookup.data?.lead && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs uppercase text-muted-foreground tracking-wide">Lead</span>
                      {lookup.data.lead.status && (
                        <Badge variant="outline" className="text-[10px]">
                          {lookup.data.lead.status}
                        </Badge>
                      )}
                    </div>
                    <button
                      type="button"
                      className="block w-full text-left group"
                      onClick={goToLead}
                    >
                      <p className="font-semibold text-sm group-hover:underline">
                        {lookup.data.lead.name || '(sem nome)'}
                      </p>
                      {lookup.data.lead.email && (
                        <p className="text-xs text-muted-foreground truncate">{lookup.data.lead.email}</p>
                      )}
                    </button>
                  </div>
                )}

                {/* Deal */}
                {lookup.data?.deal && (
                  <div className="space-y-1 pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs uppercase text-muted-foreground tracking-wide">Deal mais recente</span>
                      {lookup.data.deal.status && (
                        <Badge variant="outline" className="text-[10px]">{lookup.data.deal.status}</Badge>
                      )}
                    </div>
                    <button
                      type="button"
                      className="block w-full text-left group"
                      onClick={goToDeal}
                    >
                      <p className="font-medium text-sm group-hover:underline">
                        {lookup.data.deal.title || '(sem título)'}
                      </p>
                      {lookup.data.deal.amount != null && (
                        <p className="text-xs text-muted-foreground">
                          R$ {Number(lookup.data.deal.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                      )}
                    </button>
                  </div>
                )}

                {/* Company */}
                {(lookup.data?.lead?.company || lookup.data?.company) && (
                  <div className="space-y-1 pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs uppercase text-muted-foreground tracking-wide">Empresa</span>
                    </div>
                    <button
                      type="button"
                      className="block w-full text-left group"
                      onClick={goToCompany}
                    >
                      <p className="text-sm group-hover:underline">
                        {(lookup.data?.lead?.company?.name) ?? lookup.data?.company?.name ?? '—'}
                      </p>
                    </button>
                  </div>
                )}

                {/* Botões shortcut pra abrir profile */}
                <div className="flex gap-2 pt-2">
                  {lookup.data?.lead && (
                    <Button size="sm" variant="outline" onClick={goToLead} className="flex-1">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      Abrir lead
                    </Button>
                  )}
                  {lookup.data?.deal && (
                    <Button size="sm" variant="outline" onClick={goToDeal} className="flex-1">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      Abrir deal
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notas da ligação */}
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="incoming-notes">
              Notas da ligação
            </label>
            <Textarea
              id="incoming-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Anote o que foi conversado nessa chamada..."
              className="resize-none"
            />
            {!activityId && createActivity.isPending && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Registrando atividade...
              </p>
            )}
            {activityId && (
              <p className="text-[11px] text-success">
                Atividade criada · será concluída ao escolher o resultado
              </p>
            )}
          </div>
        </div>

          {/* Footer com resultados */}
          <div className="border-t bg-muted/20 p-4">
            <p className="text-xs text-muted-foreground mb-2">Resultado da ligação:</p>
            <div className="grid grid-cols-2 gap-2">
              {OUTCOMES.map(({ outcome, label, icon: Icon, className }) => (
                <Button
                  key={outcome}
                  variant="outline"
                  size="sm"
                  disabled={completeActivity.isPending || savedOutcome !== null}
                  onClick={() => handleOutcome(outcome)}
                  className={cn('justify-start', className, savedOutcome === outcome && 'bg-primary text-primary-foreground')}
                >
                  {completeActivity.isPending && completeActivity.variables?.outcome === outcome ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Icon className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {label}
                </Button>
              ))}
            </div>
          </div>
          </div>
          {/* fim da coluna esquerda */}

          {/* ======================================================
              Coluna direita: slot pro widget Sonax (igual SonaxFrame).
              Visualmente é só uma moldura — o widget real é injetado
              pelo script externo no document.body com position: fixed,
              e o CSS body.incoming-call-drawer-open reposiciona ele
              pra cima dessa área (top: 132px right: 32px).
              Esconde em telas pequenas (< 768px) onde não cabe.
              ====================================================== */}
          <aside
            className="hidden md:flex w-[420px] shrink-0 flex-col border-l bg-muted/30"
            aria-label="Discador Sonax"
          >
            <header className="h-11 flex-shrink-0 flex items-center gap-2 px-4 border-b bg-background">
              <div className="h-6 w-6 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                <Phone className="h-3 w-3" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Discador · Sonax
                </div>
                {(displayPhone || phone) && (
                  <div className="text-xs font-mono text-foreground truncate">
                    {displayPhone || phone}
                  </div>
                )}
              </div>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
              </span>
            </header>

            {/* Slot — o widget Sonax real flutua sobre essa área. */}
            <div className="flex-1 relative" aria-hidden />

            <footer className="h-9 flex-shrink-0 flex items-center px-4 border-t bg-background">
              <span className="text-[10px] text-muted-foreground">
                Use os botões verde / vermelho do painel acima
              </span>
            </footer>
          </aside>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
